import {
  call,
  put,
  fork,
  select,
  takeLatest,
  takeEvery,
} from 'redux-saga/effects';
import { delay } from 'redux-saga';
import { push, LOCATION_CHANGE } from 'react-router-redux';
import _ from 'lodash';
import { extractDomain } from 'vx-commons/lib/utils/stringsnippets';
import * as appActions from './actions';
import * as commonActions from 'vx-commons/lib/actions';
import request from 'vx-commons/lib/utils/request';
import requestText from 'vx-commons/lib/utils/requestText';
import {
  selectUserData,
  selectPlugins,
  selectConfig,
} from 'vx-commons/lib/selectors';
import 'whatwg-fetch';
import moment from 'moment';
import Papa from 'papaparse';
import StreamSaver from 'vx-commons/lib/utils/StreamSaver';
import {kratosLogin, handleVXAPIKratosErr } from './external_auth';


StreamSaver.mitm = `${window.location.origin}/mitm.html`;

const appIstance = extractDomain(window.location.origin);
let rootURL = '/api/v1/';

/**
 * Get api key token
 * @function
 * @param {appActions} action - The async action called from the plugin.
 */
export function* callApiKey(action) {
  const requestURL = `${rootURL}/api/api_key`;
  // Call our request helper (see 'utils/request')
  const hash = new Buffer(`${action.payload.user}:${action.payload.password}`).toString('base64');
  const response = yield call(request, requestURL, { headers: { Accept: 'application/json', 'Content-Type': 'application/json', Authorization: `Basic ${hash}` } });

  if (!response.err) {
    yield put(appActions.getApiKeySuccess(response.data));
  } else {
    yield put({
      type: 'GROWLER__SHOW',
      text: response.err.message,
      status: 'show',
    });
  }
}

/**
 * Get refreshed api key token
 * @function
 * @param {oldApiKey}
 */
let refreshOnGoing = false;
function* callApiKeyRefresh(oldApiKey) {
  // Refresh is performed only if no other refresh has been started already
  // This prevent multiple api-key refresh on dashboard update
  if (!refreshOnGoing) {
    refreshOnGoing = true;
    const requestURL = `${rootURL}/api/api_key`;

    // Call our request helper (see 'utils/request')
    const hash = new Buffer(`${oldApiKey}`);
    const response = yield call(request, requestURL, { headers: { Accept: 'application/json', 'Content-Type': 'application/json', Authorization: `Bearer ${hash}` } });

    if (!response.err) {
      console.log("API key refreshed", response.data);
      yield put(appActions.getRefreshedApiKeySuccess(response.data));
    } else if (response.err.response.status === 401) {
      // Token expired time > 10 min
      yield put(appActions.logout());
      const config = yield selectConfig();
      if(config.external_auth == null){
        yield put(push('/'));
      }else{
        kratosLogin({publicKratosAPIUrl: config.external_auth.api});
      }
    } else {
      yield put({
        type: 'GROWLER__SHOW',
        text: response.err.message,
        status: 'show',
      });
    }

    refreshOnGoing = false;
  }
}

/**
 * Get user informations
 */
export function* callProfile() {
  const userdata = yield select(selectUserData());
  const requestURL = `${rootURL}/api/my_profile`;

  const response = yield call(request, requestURL, { headers: { Authorization: `Bearer ${userdata.api_key}` }});
  if (!response.err) {
    yield put(appActions.callProfileSuccess(response.data));
  } else {
    yield put(appActions.callProfileError(response.err));
  }
}

/**
 * CRUD - centralized GET api exposed for the plugins
 * @function
 * @param {commonActions} action - The async action called from the plugin.
 *
 * Note: the parameter action.refresh is saved in the redux store
 *       refresh > 0  - The request is executed every 'refresh' seconds
 *       refresh = 0  - The request is executed once and the refresh disabled
 */

export function* callApiRead(action) {
  const userdata = yield select(selectUserData());
  const page = action.params && action.params.page
    ? `page=${action.params.page}`
    : '';
  const perPage = action.params && action.params.per_page
    ? `&per_page=${action.params.per_page}`
    : '';
  const sort = action.params && action.params.sort
    ? `&sort=${action.params.sort}`
    : '';

  // Initialize the request URL
  const requestURL = Object.keys(action.params)
    .filter(param =>
      param !== "page" && param !== "per_page" && param !== "sort" && param !== "status406asEmptyArray"
    )
    // Add parameters to the request URL query string
    .reduce((acc, param) => (param === 'q'
      ? acc + `&q=${encodeURIComponent(action.params.q)}`
      : acc + "&" + param + "=" + action.params[param]
    ), `${rootURL}${action.path}?${page}${perPage}${sort}`);

  let fetchResponse = {};

  // Try to fetch data from the requestURL defined above
  function* tryFetch(apiKey) {
    yield put(appActions.loading(true));

    fetchResponse = yield call(
      request,
      requestURL,
      { headers: { Authorization: `Bearer ${apiKey}` } },
    );

    yield put(appActions.loading(false));

    // The API call succeeds, no error occurred
    if (!fetchResponse.err) {
      yield put(
        commonActions.callReadSuccess(
          Object.assign(
            { status: 200 },
            typeof fetchResponse.data.type === "undefined"
              ? fetchResponse.data
              : fetchResponse
          ),
          action
        )
      );
    }

    // An error occurred
    if (fetchResponse.err && fetchResponse.err.response) {
      if (fetchResponse.err.response.status === 404) {
        // 404: no results have been found
        yield put(
          commonActions.callReadSuccess({ data: [], status: 404 }, action)
        );
      } else if (fetchResponse.err.response.status === 406 && action.params.status406asEmptyArray) {
        yield put(
          commonActions.callReadSuccess({ data: [], status: 406 }, action)
        );
      } else {
        yield put(commonActions.callReadError(fetchResponse.err, action));
      }  
    } else if (!navigator.onLine) {
      // If not online show an error in the top growler
      yield put({
        type: "GROWLER__SHOW",
        text: "Failed to connect to backend server",
        status: "show"
      });
    }
  }
  yield tryFetch(userdata.api_key);

  // If the error is 403
  let apiKeyRefreshAttempts = 5;
  while (
    fetchResponse.err &&
    fetchResponse.err.response &&
    fetchResponse.err.response.status === 403 &&
    apiKeyRefreshAttempts > 0
  ) {
    const errorMessage = JSON.parse(fetchResponse.err.body).message;

    if (errorMessage === "api_key invalid or expired") {
      apiKeyRefreshAttempts -= 1;

      // The API key is expired
      const userData = JSON.parse(sessionStorage.getItem('userData'));
      const oldApiKey = yield userData.api_key;

      yield callApiKeyRefresh(oldApiKey, action.path);

      const newApiKey = yield userData.api_key;
      if (newApiKey) {
        yield tryFetch(newApiKey);
      } else {
        console.log('error');
      }
    } else {
      // Generic 403 error
      yield put({
        status: 'show',
        type: 'GROWLER__SHOW',
        text: fetchResponse.err.message,
      });
      return;
    }
  }
}

/**
 * CRUD - centralized POST api exposed for the plugins
 * @function
 * @param {commonActions} action - The async action called from the plugin.
 */
export function* callApiCreate(action) {
  const userdata = yield select(selectUserData());
  const requestURL = `${rootURL}${action.path}`;
  let fetchResponse = {};

  function* tryFetch(apiKey) {
    // Set common headers and body
    let headers = {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      Authorization: `Bearer ${apiKey}`,
    };
    let body = JSON.stringify(action.item);
    // When the POST contains Javascript FormData headers and body are properly set.
    // This is needed for performing file upload POST calls
    if (action.options && action.options.formData) {
      headers = { Authorization: `Bearer ${apiKey}` };
      body = action.item;
    }

    yield put(appActions.loading(true));
    fetchResponse = yield call(
      request,
      requestURL,
      { body, headers, method: 'POST' },
    );
    yield put(appActions.loading(false));

    if (!fetchResponse.err) {
      yield put(commonActions.callCreateSuccess(fetchResponse.data, action));
    }
  }

  yield tryFetch(userdata.api_key);

  while(fetchResponse.err && fetchResponse.err.response.status === 403) {
    if (_.has(fetchResponse.err, "response")) {
      const errorMessage = JSON.parse(fetchResponse.err.body).message;
      if (fetchResponse.err.response.status === 403) {
        if (errorMessage === "api_key invalid or expired") { // IF API_KEY IS EXPIRED
          const oldApiKey = yield JSON.parse(sessionStorage.getItem('userData')).api_key;
          yield callApiKeyRefresh(oldApiKey);
          const newApiKey = yield JSON.parse(sessionStorage.getItem('userData')).api_key;

          if (newApiKey) {
            yield tryFetch(newApiKey);
          } else {
            yield put({type: 'GROWLER__SHOW', text: 'new api_key missing', status: 'show'});
          }
        } else { // IF IT'S A GENERIC 403 ERROR
          yield put({type: 'GROWLER__SHOW', text: fetchResponse.err.message, status: 'show'});
          return;
        }
      }
    }
  }

  if (_.has(fetchResponse.err, 'response')) {
    yield put(commonActions.callCreateError(fetchResponse.err, action));
  } else {
    if(!navigator.onLine) {
      yield put({
        type: 'GROWLER__SHOW',
        text: 'Failed to connect to backend server',
        status: 'show',
      });
    }
  }
}

/**
 * CRUD - centralized PUT api exposed for the plugins
 * @function
 * @param {commonActions} action - The async action called from the plugin.
 */
export function* callApiUpdate(action) {
  const userdata = yield select(selectUserData());
  const requestURL = `${rootURL}${action.path}`;
  let fetchResponse = {};

  function* tryFetch(apiKey) {
    yield put(appActions.loading(true));
    fetchResponse = yield call(
      request,
      requestURL,
      {
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          Authorization: `Bearer ${apiKey}`
        },
        method: 'PUT',
        body: JSON.stringify(action.item),
      },
    );
    yield put(appActions.loading(false));

    if (!fetchResponse.err) {
      yield put(commonActions.callUpdateSuccess(fetchResponse.data, action));
    }
  }

  yield tryFetch(userdata.api_key);

  while(fetchResponse.err && fetchResponse.err.response.status === 403) {
    if (_.has(fetchResponse.err, "response")) {
      const errorMessage = JSON.parse(fetchResponse.err.body).message;
      if (fetchResponse.err.response.status === 403) {
        if (errorMessage === "api_key invalid or expired") { // IF API_KEY IS EXPIRED
          const oldApiKey = yield JSON.parse(sessionStorage.getItem('userData')).api_key;
          yield callApiKeyRefresh(oldApiKey);
          const newApiKey = yield JSON.parse(sessionStorage.getItem('userData')).api_key;

          if (newApiKey) {
            yield tryFetch(newApiKey);
          } else {
            yield put({type: 'GROWLER__SHOW', text: 'new api_key missing', status: 'show'});
          }
        } else { // IF IT'S A GENERIC 403 ERROR
          yield put({type: 'GROWLER__SHOW', text: fetchResponse.err.message, status: 'show'});
          return;
        }
      }
    }
  }

  if (_.has(fetchResponse.err, 'response')) {
    yield put(commonActions.callUpdateError(fetchResponse.err, action));
  } else {
    if(!navigator.onLine) {
      yield put({
        type: 'GROWLER__SHOW',
        text: 'Failed to connect to backend server',
        status: 'show',
      });
    }
  }
}

/**
 * CRUD - centralized DELETE api exposed for the plugins
 * @function
 * @param {commonActions} action - The async action called from the plugin.
 */
export function* callApiDelete(action) {
  const userdata = yield select(selectUserData());
  const requestURL = `${rootURL}${action.path}`;
  let fetchResponse = {};

  function* tryFetch(apiKey) {
    yield put(appActions.loading(true));
    fetchResponse = yield call(
      request,
      requestURL,
      {
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          Authorization: `Bearer ${apiKey}`
        },
        method: 'DELETE',
        body: JSON.stringify(action.item),
      },
    );
    yield put(appActions.loading(false));

    if (!fetchResponse.err) {
      yield put(commonActions.callDeleteSuccess(fetchResponse.data, action));
    }
  }

  yield tryFetch(userdata.api_key);

  while(fetchResponse.err && fetchResponse.err.response.status === 403) {
    if (_.has(fetchResponse.err, "response")) {
      const errorMessage = JSON.parse(fetchResponse.err.body).message;
      if (fetchResponse.err.response.status === 403) {
        // IF API_KEY IS EXPIRED
        if (errorMessage === "api_key invalid or expired") {
          const oldApiKey = yield JSON.parse(sessionStorage.getItem('userData')).api_key;
          yield callApiKeyRefresh(oldApiKey);
          const newApiKey = yield JSON.parse(sessionStorage.getItem('userData')).api_key;

          if (newApiKey) {
            yield tryFetch(newApiKey);
          } else {
            yield put({type: 'GROWLER__SHOW', text: 'new api_key missing', status: 'show'});
          }
        } else {
          // IF IT'S A GENERIC 403 ERROR
          yield put({type: 'GROWLER__SHOW', text: fetchResponse.err.message, status: 'show'});
          return;
        }
      }
    }
  }

  if (_.has(fetchResponse.err, 'response')) {
    yield put(commonActions.callDeleteError(fetchResponse.err, action));
  } else {
    if(!navigator.onLine) {
      yield put({
        type: 'GROWLER__SHOW',
        text: 'Failed to connect to backend server',
        status: 'show',
      });
    }
  }
}


/**
 * CRUD - centralized GET iterating through all available pages, convert data in CSV and send them
 * to a writeStream to save them in a local file page by page
 * @function
 * @param {commonActions} action - The async action called from the plugin.
 * action include the following fields:
 *    - params:   params to be used in the GET. Important ones are the query and the fields to be retrieved
 *    - path:     request path
 *    - filename: part of the file name used so save the data locally
 *    - fields:   array of fields used in the main page necessary to write the header and position the value in the
 *                right place (column)
 */
function* abortDownload() {
  yield put({ type: 'GROWLER__HIDE', text: `ABORTED BY THE USER`, status: 'show', timeout: 0});
  loop = false;
  console.log("ABORT");
}

var loop;
var writer;
export function* callApiReadExport(action) {
  const now = moment().format('YYYYMMDD-hhmmss');
  const userdata = yield select(selectUserData());

  let page = action.params ? action.params.page : 1;
  const per_page = action.params ? action.params.per_page : 5000;

  let requestURL = `${rootURL}${action.path}?`;
  const filename = `export_${now}.csv`;
  const fields = action.fields ? action.fields : [];

  Object.keys(action.params).forEach((p) => {
    if (p !== 'page' && p !== 'per_page') {
      if (p === 'query') {
        const query = action.params && action.params.q ? encodeURIComponent(action.params.q) : '';
        requestURL = requestURL + `&q=${query}`
      } else {
        requestURL = requestURL + '&' + p + '=' + action.params[p];
      }
    }
  });

  loop = true;
  let totpage = 1;
  let fetchResponse = {};
  let sentRequestURL = '';

  writer = null;
  let encoder = new TextEncoder();
  // csv_array: array used to export CSV data
  // Inserts the headers
  let csv_array = [fields.map((field) => (field.label))];
  let progress = 0;

  const generate_row = (item) => (
    fields.map((field) => (
      item[field.api_name] !== undefined
        ? item[field.api_name]
        : ''
    ))
  );

  const addRows = (item) => {
    csv_array.push(generate_row(item));
  }

  yield put({ type: 'GROWLER__SHOW', text: `Downloading ${action.resourceName}`, status: 'show', timeout: 0 });




  function* tryFetch(apiKey) {
    sentRequestURL =  requestURL + `&per_page=${per_page}&page=${page}`;
    yield put(appActions.loading(true));
    fetchResponse = yield call(request, sentRequestURL, { headers: { Authorization: `Bearer ${apiKey}` }});
    yield put(appActions.loading(false));

    if (!fetchResponse.err) {
      if (page === 1) {
        // Once the first page is retrieved, create the write stream to save the data
        writer = StreamSaver.createWriteStream(filename).getWriter();

        if (fetchResponse.data !== undefined && fetchResponse.data.pagination !== undefined) {
          // Calculates the total number of pages must be retrieved
          totpage = Math.ceil(fetchResponse.data.pagination.total / per_page);
        }
      }
      progress = (page / totpage * 100).toFixed(1);

      yield put({
        type: 'GROWLER__SHOW',
        text: `Downloading ${action.resourceName} ${progress}%`,
        status: 'show',
        timeout: 0,
        action: abortDownload
      });

      // If first page do not clean the csv_array because it contains the headers
      if (page > 1) {
        csv_array = [];
      }

      // Converts every retrieved item in a array of values
      // the order is given by the field order (we are sure it is the same of the headers)
      fetchResponse.data.data.forEach(addRows);

      // Converts the two dimensional array in CSV
      let csv = Papa.unparse(csv_array);
      // Encodes the text
      let uint8array = encoder.encode(csv+'\n');
      // Writes the test to the stream
      writer.write(uint8array);

      // This is an HACK and try to cope with the issue of write not finished before next request is sent
      yield delay(1000);
      // This is an HACK ****
      console.log('callApiReadExport page=', page, '/', totpage, 'total records=', fetchResponse.data.pagination.total);

      // Increment the page
      page = page + 1;
      if (page > totpage) {
        // Page > totpage: process finished!
        // Closes the stream and exit from the loop
        loop = false;
        writer.close();
        progress = 100;
        yield put({ type: 'GROWLER__SHOW', text: `Download of ${action.resourceName} completed!`, status: 'show'});
      }
    }
  }

  while(loop) {
    yield tryFetch(userdata.api_key);

    while(fetchResponse.err && fetchResponse.err.response.status === 403) {
      if (_.has(fetchResponse.err, "response")) {
        const errorMessage = JSON.parse(fetchResponse.err.body).message;
        if (fetchResponse.err.response.status === 403) {
          if (errorMessage === "api_key invalid or expired") { // IF API_KEY IS EXPIRED
            const oldApiKey = yield JSON.parse(sessionStorage.getItem('userData')).api_key;
            yield callApiKeyRefresh(oldApiKey);
            const newApiKey = yield JSON.parse(sessionStorage.getItem('userData')).api_key;

            if (newApiKey) {
              yield tryFetch(newApiKey);
            } else {
              yield put({type: 'GROWLER__SHOW', text: 'new api_key missing', status: 'show'});
            }
          } else { // IF IT'S A GENERIC 403 ERROR
            yield put({type: 'GROWLER__SHOW', text: fetchResponse.err.message, status: 'show'});
            return;
          }
        }
      }
    }

    if (fetchResponse.err) {
      // TODO 406 --> qualcosa non va bene (esempio email)
      if (_.has(fetchResponse.err, 'response')) {
        if (fetchResponse.err.response.status === 404) {
          loop = false;
          if (writer !== null) {
            writer.close();
          }
          yield put(commonActions.callReadSuccess({ data: [], status: 404 }, action));
          yield put({ type: 'GROWLER__SHOW', text: 'There is no data to download', status: 'show' });
        } else {
          yield put(commonActions.callReadError(fetchResponse.err, action));
          yield put({ type: 'GROWLER__SHOW', text: 'Error reading data. Download failed!', status: 'show' });
        }
      } else if (fetchResponse.err === 'TypeError: Failed to fetch'){
        console.log("RETRY", fetchResponse.err);
        yield delay(1000);
      } else {
        console.log("RETRY", fetchResponse.err.message);
        loop = false;
        if (writer !== null) {
          writer.close();
        }
        yield put({ type: 'GROWLER__SHOW', text: 'Failed to connect to backend server. Download failed!', status: 'show'});
      }
    }
  }
}

/**
 * SaveSettings - Save plugin settings
 * @function
 * @param {name, settings, username} action - The async action called from the plugin.
 */
export function* saveSettings(action) {
  yield put(commonActions.callUpdate(
    '/api/user/settings',
    `settings/${action.name}`,
    {
      name: `${appIstance}/${action.name}`,
      settings: JSON.stringify(action.settings),
    })
  );
}

/**
 * loadSettings - Load plugin settings
 * @function
 * @param {name} action - The async action called from the plugin.
 */
export function* loadSettings(action) {
  const userdata = yield select(selectUserData());
  const query = `name:${appIstance}/${action.name},username:${userdata.username}`;

  yield put(commonActions.callRead(
    '/api/user/settings',
    `settings/${action.name}`,
    {
      page: 1,
      per_page: 1000,
      q: query,
    })
  );
}

/**
 * deleteSettings - Delete plugin settings
 * @function
 * @param {name} action - The async action called from the plugin.
 */
export function* deleteSettings(action) {
  yield put(commonActions.callDelete(
    '/api/user/settings',
    `settings/${action.name}`,
    {
      id: action.id,
    })
  );
}

const requirePlugin = (pluginsconfig) => (pluginItem) => new Promise((resolve) => {
  require.ensure([], require => {
    const plugin = require(`../../plugins/${pluginItem.folder}/index.js`);
    let config = {};
    try {
      if (! pluginsconfig.err) {
        const defaultConfig = pluginsconfig.data.plugins.filter(item => (item.name === pluginItem.name));
        if (defaultConfig.length > 0) {
          config = plugin.init(defaultConfig[0].defaults);
        } else {
          config = plugin.init();
        }
      } else {
        config = plugin.init();
      }
      // if !config means plugin would not be installed
      resolve({
        ...pluginItem,
        state: config ? 'installed' : 'not_installed',
        config,
      });
    } catch (e) {
      resolve({
        ...pluginItem,
        state: 'init_error',
        error: e,
      });
    }
  });
});

/**
 * initPlugins - saga for return to homepage
 * @function
 */
export function* initPlugins() {
  // Loads config from REDUX store
  let config = yield select(selectConfig());
  const loaded = yield select(selectPlugins());

  // The first time the config is loaded from it location
  if (!config) {
    const result = yield call(request, '/config.json', { headers: { Accept: 'application/json', 'Content-Type': 'application/json' } });
    config = {
      ...result.data,
      locale: result.data.locale || 'en',
    };

    if (!config.err) {
      yield put(commonActions.loadConfigSuccess(config));
    } else {
      console.log('Error getting config.json', config);
    }
  }

  rootURL = config.repository || rootURL;

  // When the application starts plug-ins are initialized
  if (loaded.length === 0) {
    const packages = require.context('../../plugins/', true, /[^/]+\/package\.json$/);
    let plugins = [];
    yield plugins = packages.keys().reduce((acc, path) => {
      const folder = /^\.\/([^/]+)/.exec(path)[1];
      const pkg = packages(path);

      // If name is not defined we do not like the package
      if ((pkg.name || '') === '') {
        console.error(`Plugin in ${folder} doesn't have name set in package.json`);
        return acc;
      }

      // Check if the plugin is enabled / disabled in the config
      let enable = true;
      let config_status = 'Auto discovered';
      config.plugins.forEach((item) => {
        if (item.name === pkg.name) {
          enable = item.enable;
          config_status = 'Configured';
        }
      })

      // Pick only selected properties and add some defaults
      return acc.concat(_.merge({
        folder,
        name: '',
        enable: enable,
        config_status: config_status,
        description: '',
        version: '0.0.0',
        state: 'not_installed',
        dependencies: {},
      }, _.pick(pkg, [
        'name',
        'description',
        'version',
        'dependencies',
      ])));
    }, []);

    const values = yield Promise.all(plugins.map(requirePlugin({ data: config })));
    yield put(appActions.initPluginsSuccess(values));
  } else {
    yield put(appActions.checkPluginsSuccess(loaded));
  }
}

/**
 * callProfileSuccess
 * @function
 */
export function* callProfileSuccess() {
  const userdata = yield select(selectUserData());
  yield put(commonActions.appLogin(userdata));
  yield put(push('/d/homepage'));
}

export function* requestPasswordRecovery(action) {
  let response = yield call(
    request,
    `${rootURL}/api/user/password?username_or_email=${encodeURIComponent(action.email)}&url=${window.location.origin}/password_recovery&usertype=user`,
  );
  if (!response.err) {
    yield put({type: 'GROWLER__SHOW', text: 'Check your e-mail box', status: 'show'});
  }
  if (action.redirect === true) {
    yield put(push('/d/homepage'));
  }
  return;
}

export function* passwordRecovery(action) {
  let response = yield call(
    request,
    `${rootURL}/api/user/password`,
    {
      method: 'PUT',
      body: JSON.stringify({
        password: action.password,
        recovery_code:action.recovery_code,
        usertype: "user"
      }),
    },
  );
  if (!response.err) {
    yield put({type: 'GROWLER__SHOW', text: 'Your password has been successfully reset', status: 'show'});
    yield put(push('/d/homepage'));
    return;
  } else {
    yield put({type: 'GROWLER__SHOW', text: `Cannot reset your password: ${JSON.parse(response.err.body).message}`, status: 'show'});
  }
}

export function* login(action) {
  yield put(appActions.loading(true));
  const hash = new Buffer(`${action.username}:${action.password}`).toString('base64');
  let response = yield call(
    request,
    `${rootURL}/api/api_key`,
    { headers:
        { Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
          'Content-Type': 'application/json',
          Authorization: `Basic ${hash}`
        },
        credentials: "include"
    }
  );

  if (response.err) {
    const config = yield select(selectConfig());
    if(config.external_auth != null){
      return handleVXAPIKratosErr({publicKratosAPIUrl: config.external_auth.api, publicKratosAppUrl: config.external_auth.app}, response.err);
    }

    yield put(appActions.loginFailed(response.err));
    yield put(appActions.loading(false));
    yield put({type: 'GROWLER__SHOW', text: response.err.message, status: 'show'});
    return;
  }

  const api_key = response.data.api_key;
  let api_info = {};
  response = yield call(request, `${rootURL}/api`);
  if (!response.err) {
    api_info = response.data;
  }
  response = yield call(request, `${rootURL}/api/my_profile`, { headers: { Authorization: `Bearer ${api_key}` }});

  if(response.err) {
    const config = yield select(selectConfig());
    if(config.external_auth != null){
      return handleVXAPIKratosErr({publicKratosAPIUrl: config.external_auth.api, publicKratosAppUrl: config.external_auth.app}, response.err);
    }

    yield put(appActions.loading(false));
    yield put({type: 'GROWLER__SHOW', text: 'Cannot get user profile', status: 'show'});
    return;
  }

  const profile = response.data;

  if (['admin', 'network_operator', 'network_operator_support', 'service_provider', 'service_provider_support', 'sales', 'installer_manager'].indexOf(profile.role) === -1) {
    yield put(appActions.loading(false));
    yield put({type: 'GROWLER__SHOW', text: `You cannot access with ${profile.role} credential`, status: 'show'});
    return;
  }
  yield put(appActions.loginSuccess(profile));
  yield put(appActions.apiInfoSuccess(api_info));
  yield put(commonActions.appLogin(profile));
  yield put(push(window.location.pathname + window.location.search));
  yield put(appActions.loading(false));
  return;
}

/**
 * loadHelp - saga for loading Help (for page and widgets)
 * @function
 */
export function* loadHelp(action) {
  console.log(action)
  const helpPage = yield call(requestText, action.help, { headers: { 'Accept': 'text/html', 'Content-Type': 'text/html' } });
  if (!helpPage.err) {
    yield put(commonActions.loadHelpSuccess(helpPage,action.helptype));
  } else {
    console.log('Error getting help page', helpPage);
  }
}

/**


/**
 * Root saga manages watcher lifecycle
 */
export function* appSaga() {
  // Fork watcher so we can continue execution
  yield* [
    fork(takeLatest, appActions.GET_TOKEN, callApiKey),
    fork(takeLatest, appActions.GET_TOKEN_SUCCESS, callProfile),
    fork(takeEvery, appActions.GET_PROFILE_SUCCESS, callProfileSuccess),
    fork(takeLatest, commonActions.API_CREATE, callApiCreate),
    fork(takeEvery, commonActions.API_READ, callApiRead),
    fork(takeEvery, commonActions.API_READ_EXPORT, callApiReadExport),
    fork(takeLatest, commonActions.API_UPDATE, callApiUpdate),
    fork(takeLatest, commonActions.API_DELETE, callApiDelete),
    fork(takeEvery, commonActions.SAVE_SETTINGS, saveSettings),
    fork(takeEvery, commonActions.LOAD_SETTINGS, loadSettings),
    fork(takeEvery, commonActions.DELETE_SETTINGS, deleteSettings),
    fork(takeEvery, commonActions.LOAD_HELP, loadHelp),
    fork(takeLatest, LOCATION_CHANGE, initPlugins),
    fork(takeLatest, appActions.LOGIN, login),
    fork(takeLatest, appActions.REQUEST_PASSWORD_RECOVERY, requestPasswordRecovery),
    fork(takeLatest, appActions.PASSWORD_RECOVERY, passwordRecovery),
  ];
}

export default appSaga;



// WEBPACK FOOTER //
// ./src/containers/App/sagas.js